// doorFrame.js
import {
    MeshBuilder,
    StandardMaterial,
    Color3,
    Vector3,
    Axis,
    Space,
    Mesh
  } from '@babylonjs/core';
  
  import {
    createWhiteCeramicMaterial,
    createWhiteDoorMaterial,
    createGreyQuartzStoneTileMaterial
  } from './bathroomMaterials';
  
  /******************************************************
   * forceUpdateBoundingInfo(mesh)
   * Ensures bounding info is fresh before measuring.
   ******************************************************/
  function forceUpdateBoundingInfo(mesh) {
    mesh.computeWorldMatrix(true);
    mesh.refreshBoundingInfo();
  }
  
  /******************************************************
   * getBoundingBoxInWorld(mesh)
   * Returns { min: Vector3, max: Vector3 } in WORLD coords
   ******************************************************/
  function getBoundingBoxInWorld(mesh) {
    forceUpdateBoundingInfo(mesh);
    const bb = mesh.getBoundingInfo().boundingBox;
  
    // Local min/max
    const localMin = bb.minimum;
    const localMax = bb.maximum;
  
    // Convert them to world coords
    const wmat = mesh.getWorldMatrix();
    const worldMin = Vector3.TransformCoordinates(localMin, wmat);
    const worldMax = Vector3.TransformCoordinates(localMax, wmat);
  
    return { min: worldMin, max: worldMax };
  }
  
  /******************************************************
   * getBoundingBoxCornersInWorld(mesh)
   * Returns an array of 8 corners (Vector3) in WORLD coords
   ******************************************************/
  function getBoundingBoxCornersInWorld(mesh) {
    forceUpdateBoundingInfo(mesh);
    const bb = mesh.getBoundingInfo().boundingBox;
    // Babylon already stores the 8 corners in world space:
    // boundingBox.vectorsWorld is an array of 8 Vector3
    // (but we clone them for safety)
    return bb.vectorsWorld.map((corner) => corner.clone());
  }
  
  /******************************************************
   * getMeshAngleInXZByCorners(mesh)
   * 1) Retrieve bounding-box corners in world coords.
   * 2) Find the pair of corners with the largest
   *    distance in the XZ plane.
   * 3) Return the angle of that line segment w.r.t. X axis.
   ******************************************************/
  function getMeshAngleInXZByCorners(mesh) {
    const corners = getBoundingBoxCornersInWorld(mesh);
    let maxDistSq = 0;
    let furthestPair = [corners[0], corners[0]];
  
    // Find pair of corners with largest distance in XZ
    for (let i = 0; i < corners.length; i++) {
      for (let j = i + 1; j < corners.length; j++) {
        const dx = corners[j].x - corners[i].x;
        const dz = corners[j].z - corners[i].z;
        const distSq = dx * dx + dz * dz;
  
        if (distSq > maxDistSq) {
          maxDistSq = distSq;
          furthestPair = [corners[i], corners[j]];
        }
      }
    }
  
    // Vector representing the furthest edge in XZ
    const v = furthestPair[1].subtract(furthestPair[0]);
  
    // Angle in XZ relative to the X axis
    const angle = Math.atan2(v.z, v.x);
    return angle;
  }
  
  /******************************************************
   * doorInsideFrameXZ(doorMesh, frameMesh)
   * Returns true if the door's bounding box 
   * is fully inside the frame's bounding box 
   * in the XZ plane (ignoring Y).
   ******************************************************/
  function doorInsideFrameXZ(doorMesh, frameMesh) {
    const doorBB = getBoundingBoxInWorld(doorMesh);
    const frameBB = getBoundingBoxInWorld(frameMesh);
  
    // Compare XZ min/max
    return (
      doorBB.min.x >= frameBB.min.x &&
      doorBB.max.x <= frameBB.max.x &&
      doorBB.min.z >= frameBB.min.z &&
      doorBB.max.z <= frameBB.max.z
    );
  }
  
  /******************************************************
   * getBoundingBoxCenterWorld(mesh)
   * Returns bounding-box center in WORLD coords
   ******************************************************/
  function getBoundingBoxCenterWorld(mesh) {
    forceUpdateBoundingInfo(mesh);
    const bb = mesh.getBoundingInfo().boundingBox;
    const centerLocal = bb.minimum.add(bb.maximum).scale(0.5);
    return Vector3.TransformCoordinates(centerLocal, mesh.getWorldMatrix());
  }
  
  /******************************************************
   * alignMeshCenters(doorMesh, frameMesh)
   * Shifts frame so its bounding-box center 
   * matches the door's bounding-box center.
   ******************************************************/
  function alignMeshCenters(doorMesh, frameMesh) {
    const doorCenter = getBoundingBoxCenterWorld(doorMesh);
    const frameCenter = getBoundingBoxCenterWorld(frameMesh);
    const offset = doorCenter.subtract(frameCenter);
    frameMesh.position.addInPlace(offset);
  }
  
  /******************************************************
   * alignDoorFrameXZ(doorMesh, frameMesh)
   * 
   * 1) Get angle of door in XZ by analyzing its bounding-
   *    box corners (largest edge in XZ).
   * 2) Get angle of frame the same way.
   * 3) Rotate frame around Y by the difference.
   * 4) Then align bounding-box centers.
   * 5) Check if door bounding box is inside 
   *    the frame's bounding box in XZ. If not, 
   *    rotate by ±90° (Math.PI/2) and re-align.
   ******************************************************/
  function alignDoorFrameXZ(doorMesh, frameMesh) {
    // 1) Door angle in XZ
    const angleDoor = getMeshAngleInXZByCorners(doorMesh);
  
    // 2) Frame angle in XZ
    const angleFrame = getMeshAngleInXZByCorners(frameMesh);
  
    // 3) Rotate the frame around Y by the difference
    const delta = angleDoor - angleFrame;
    frameMesh.rotate(Axis.Y, delta, Space.WORLD);
  
    // 4) Align bounding-box centers (post-rotation)
    alignMeshCenters(doorMesh, frameMesh);
  
    // 5) Check bounding boxes in XZ
    if (!doorInsideFrameXZ(doorMesh, frameMesh)) {
      // It's likely off by 90 degrees. Let's rotate 
      // by +PI/2 or -PI/2. Usually + is enough, 
      // but you may want to try both.
  
      // Try rotating +90 deg
      frameMesh.rotate(Axis.Y, Math.PI / 2, Space.WORLD);
      alignMeshCenters(doorMesh, frameMesh);
  
      // Check again
      if (!doorInsideFrameXZ(doorMesh, frameMesh)) {
        // revert +90, try -90
        frameMesh.rotate(Axis.Y, -Math.PI / 2, Space.WORLD); // revert
        frameMesh.rotate(Axis.Y, -Math.PI / 2, Space.WORLD); // net -90
        alignMeshCenters(doorMesh, frameMesh);
      }
    }
  }
  
  /******************************************************
   * matchDoorFrameToDoor(doorMesh, frameMesh)
   * Convenience wrapper that calls alignDoorFrameXZ
   ******************************************************/
  export function matchDoorFrameToDoor(doorMesh, frameMesh) {
    alignDoorFrameXZ(doorMesh, frameMesh);
  }
  
  /******************************************************
   * createDoorFrame(scene, rawW, rawH, rawD)
   * Sort dims so largest=height(Y), smallest=depth(Z),
   * middle=width(X). Then build the frame around (0,0,0).
   ******************************************************/
  export function createDoorFrame(scene, rawW, rawH, rawD) {
    // Sort raw dims: smallest->largest
    const sorted = [rawW, rawH, rawD].sort((a, b) => a - b);
    const actualDepth = sorted[0];
    const actualWidth = sorted[1];
    const actualHeight = sorted[2];
  
    const frameThickness = 0.05;
    const frameDepth = actualDepth + 0.05;
  
    // -- TOP --
    const frameTop = MeshBuilder.CreateBox("frameTop", {
      width: actualWidth + 2 * frameThickness, // X
      height: frameThickness,                  // Y
      depth: frameDepth                        // Z
    }, scene);
    frameTop.position.y = actualHeight / 2 + frameThickness / 2;
  
    // -- BOTTOM --
    const frameBottom = MeshBuilder.CreateBox("frameBottom", {
      width: actualWidth + 2 * frameThickness,
      height: frameThickness,
      depth: frameDepth
    }, scene);
    frameBottom.position.y = -actualHeight / 2 - frameThickness / 2;
  
    // -- LEFT --
    const frameLeft = MeshBuilder.CreateBox("frameLeft", {
      width: frameThickness,
      height: actualHeight,
      depth: frameDepth
    }, scene);
    frameLeft.position.x = -(actualWidth / 2 + frameThickness / 2);
  
    // -- RIGHT --
    const frameRight = MeshBuilder.CreateBox("frameRight", {
      width: frameThickness,
      height: actualHeight,
      depth: frameDepth
    }, scene);
    frameRight.position.x = actualWidth / 2 + frameThickness / 2;
  
    // Merge them
    const mergedFrame = Mesh.MergeMeshes(
      [frameTop, frameBottom, frameLeft, frameRight],
      true,  // dispose originals
      false, // 32-bit indices
      undefined,
      false, // subdiv
      true   // multiMaterial
    );
    mergedFrame.name = "doorFrame";
  
    // Red mat
    const whiteDoorMaterial = createWhiteDoorMaterial(scene);
    mergedFrame.material = whiteDoorMaterial;
  
    return mergedFrame;
  }
  